import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SubscriptionDetailsModal from './components/SubscriptionDetailsModal';
import {
  adminSubscriptionService,
  type AdminSubscription,
  type SubscriptionInterval,
  type SubscriptionStatus,
} from '../../services/adminSubscriptionService';
import {
  adminSubscriptionReminderService,
  type SubscriptionReminderAgendaEntry,
  type SubscriptionReminderOverview,
} from '../../services/adminSubscriptionReminderService';
import { adminPaymentLinkService } from '../../services/adminPaymentLinkService';
import { clientService, type Client } from '../../services/clientService';
import { currencyService } from '../../services/currencyService';
import { getAllServices, type Service } from '../../services/servicesStore';
import {
  adminTeamWorkspaceService,
  type TeamSubscriptionRecord,
} from '../../services/adminTeamWorkspaceService';

const STATUS_FILTERS: Array<{ value: 'all' | SubscriptionStatus; label: string }> = [
  { value: 'all', label: 'Tous statuts' },
  { value: 'trial', label: 'Essai' },
  { value: 'active', label: 'Actif' },
  { value: 'paused', label: 'En pause' },
  { value: 'past_due', label: 'En retard' },
  { value: 'canceled', label: 'Annule' },
  { value: 'expired', label: 'Expire' },
];

const INTERVAL_FILTERS: Array<{ value: 'all' | SubscriptionInterval; label: string }> = [
  { value: 'all', label: 'Toutes frequences' },
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
];

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trial: 'Essai',
  active: 'Actif',
  paused: 'En pause',
  past_due: 'En retard',
  canceled: 'Annule',
  expired: 'Expire',
};

const STATUS_BADGES: Record<SubscriptionStatus, string> = {
  trial: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  past_due: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-100 text-orange-700',
};

const INTERVAL_LABELS: Record<SubscriptionInterval, string> = {
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  yearly: 'Annuel',
};

const OPS_FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'due_soon', label: 'Echeance <= 10j' },
  { value: 'at_risk', label: 'A risque' },
  { value: 'mandate_pending', label: 'Mandat pending' },
];

const formatDate = (value?: string) => {
  if (!value) {
    return 'Non renseigne';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Non renseigne';
  }

  return parsed.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return 'Non renseigne';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Non renseigne';
  }

  return parsed.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const dateKeyFromValue = (value?: string) => {
  if (!value) {
    return '';
  }

  const directMatch = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (directMatch) {
    return directMatch[1];
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().slice(0, 10);
};

const getDaysUntil = (value?: string) => {
  const targetKey = dateKeyFromValue(value);
  if (!targetKey) {
    return Number.POSITIVE_INFINITY;
  }

  const todayKey = new Date().toISOString().slice(0, 10);
  const targetDate = new Date(`${targetKey}T00:00:00.000Z`);
  const todayDate = new Date(`${todayKey}T00:00:00.000Z`);
  return Math.round((targetDate.getTime() - todayDate.getTime()) / 86400000);
};

const getRiskMeta = (subscription: AdminSubscription) => {
  const daysUntil = getDaysUntil(subscription.nextBillingDate);
  const recurringStatus = String(subscription.recurringStatus || '').toLowerCase();
  const billingStatus = String(subscription.billingStatus || '').toLowerCase();
  const serviceStatus = String(subscription.serviceStatus || '').toLowerCase();

  if (
    subscription.status === 'past_due' ||
    billingStatus === 'overdue' ||
    billingStatus === 'unpaid' ||
    daysUntil < 0
  ) {
    return {
      level: 'critical',
      label: 'Critique',
      classes: 'bg-red-100 text-red-700',
      reason: daysUntil < 0 ? 'Echeance depassee' : 'Paiement en retard',
    };
  }

  if (recurringStatus.includes('pending') || !subscription.paymentLinkKey.trim()) {
    return {
      level: 'warning',
      label: 'Mandat',
      classes: 'bg-amber-100 text-amber-800',
      reason: recurringStatus.includes('pending')
        ? 'Mandat non active'
        : 'Lien paiement manquant',
    };
  }

  if (daysUntil <= 10 || serviceStatus === 'onboarding' || serviceStatus === 'pending_setup') {
    return {
      level: 'watch',
      label: 'Suivi',
      classes: 'bg-blue-100 text-blue-700',
      reason:
        daysUntil <= 10
          ? `Renouvellement dans ${Math.max(daysUntil, 0)} j`
          : 'Onboarding a suivre',
    };
  }

  return {
    level: 'healthy',
    label: 'Stable',
    classes: 'bg-emerald-100 text-emerald-700',
    reason: 'RAS sur les 10 prochains jours',
  };
};

const getMonthStart = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

const addMonths = (date: Date, months: number) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));

const buildCalendarDays = (referenceDate: Date) => {
  const monthStart = getMonthStart(referenceDate);
  const startWeekday = (monthStart.getUTCDay() + 6) % 7;
  const gridStart = new Date(monthStart);
  gridStart.setUTCDate(monthStart.getUTCDate() - startWeekday);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setUTCDate(gridStart.getUTCDate() + index);
    return {
      key: date.toISOString().slice(0, 10),
      dayNumber: date.getUTCDate(),
      inMonth: date.getUTCMonth() === monthStart.getUTCMonth(),
      isToday: date.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10),
    };
  });
};

const SubscriptionManager = () => {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [reminderOverview, setReminderOverview] = useState<SubscriptionReminderOverview | null>(
    null
  );
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SubscriptionStatus>('all');
  const [intervalFilter, setIntervalFilter] = useState<'all' | SubscriptionInterval>('all');
  const [opsFilter, setOpsFilter] =
    useState<'all' | 'due_soon' | 'at_risk' | 'mandate_pending'>('all');
  const [selectedSubscription, setSelectedSubscription] =
    useState<AdminSubscription | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'create'>('view');
  const [isRunningReminderSweep, setIsRunningReminderSweep] = useState(false);
  const [sendingReminderForId, setSendingReminderForId] = useState<string>('');
  const [calendarMonthOffset, setCalendarMonthOffset] = useState(0);
  const [modalActionInFlight, setModalActionInFlight] = useState('');
  const [teamRecords, setTeamRecords] = useState<Record<string, TeamSubscriptionRecord>>({});

  const loadData = async () => {
    setIsLoading(true);

    try {
      const [subscriptionsData, clientsData, servicesData, reminderData, teamRows] = await Promise.all([
        adminSubscriptionService.getSubscriptions(true),
        clientService.getClients(true),
        getAllServices(true),
        adminSubscriptionReminderService.getOverview().catch((error) => {
          console.error(error);
          return null;
        }),
        adminTeamWorkspaceService.getRecords().catch((error) => {
          console.error(error);
          return [];
        }),
      ]);

      setSubscriptions(subscriptionsData);
      setClients(clientsData);
      setServices(servicesData);
      setReminderOverview(reminderData);
      setTeamRecords(
        teamRows.reduce<Record<string, TeamSubscriptionRecord>>((acc, row) => {
          acc[row.subscriptionId] = row;
          return acc;
        }, {})
      );
      return subscriptionsData;
    } catch (error) {
      console.error(error);
      toast.error('Erreur de chargement des abonnements');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);
  const normalizedQuery = query.trim().toLowerCase();

  const filteredSubscriptions = subscriptions.filter((subscription) => {
    const matchesStatus =
      statusFilter === 'all' ? true : subscription.status === statusFilter;
    const matchesInterval =
      intervalFilter === 'all' ? true : subscription.interval === intervalFilter;
    const riskMeta = getRiskMeta(subscription);
    const matchesOps =
      opsFilter === 'all'
        ? true
        : opsFilter === 'due_soon'
          ? getDaysUntil(subscription.nextBillingDate) <= 10
          : opsFilter === 'at_risk'
            ? riskMeta.level === 'critical' || riskMeta.level === 'warning'
            : String(subscription.recurringStatus || '').toLowerCase().includes('pending');
    const matchesQuery =
      !normalizedQuery ||
      [
        subscription.subscriptionNumber,
        subscription.clientName,
        subscription.clientEmail,
        subscription.clientCompany,
        subscription.serviceName,
        subscription.linkedOrderNumber,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedQuery));

    return matchesStatus && matchesInterval && matchesOps && matchesQuery;
  });

  const metrics = subscriptions.reduce(
    (accumulator, subscription) => {
      accumulator.total += 1;
      accumulator.revenue += currencyService.convertToDisplayAmount(
        subscription.amount,
        subscription.currencyCode,
        displayCountry
      );

      if (subscription.status === 'active') {
        accumulator.active += 1;
      }

      if (subscription.status === 'past_due') {
        accumulator.pastDue += 1;
      }

      if (subscription.status === 'paused') {
        accumulator.paused += 1;
      }

      return accumulator;
    },
    {
      total: 0,
      active: 0,
      pastDue: 0,
      paused: 0,
      revenue: 0,
    }
  );

  const calendarReferenceDate = addMonths(new Date(), calendarMonthOffset);
  const calendarDays = buildCalendarDays(calendarReferenceDate);
  const agendaByDate = (reminderOverview?.agenda || []).reduce<Record<string, SubscriptionReminderAgendaEntry[]>>(
    (accumulator, entry) => {
      const key = dateKeyFromValue(entry.date);
      if (!key) {
        return accumulator;
      }
      accumulator[key] = accumulator[key] || [];
      accumulator[key].push(entry);
      return accumulator;
    },
    {}
  );

  const handleOpenCreate = () => {
    setSelectedSubscription(adminSubscriptionService.createEmptySubscription());
    setModalMode('create');
  };

  const handleOpenView = (subscription: AdminSubscription) => {
    setSelectedSubscription(subscription);
    setModalMode('view');
  };

  const handleSave = async (subscription: AdminSubscription) => {
    try {
      const isCreateMode = modalMode === 'create';
      let subscriptionToSave = subscription;
      let createdPaymentLink = false;
      let paymentEmailSent = false;
      let paymentEmailError = '';

      if (
        isCreateMode &&
        !subscription.paymentLinkKey.trim() &&
        subscription.clientName.trim() &&
        subscription.clientEmail.trim() &&
        subscription.serviceName.trim() &&
        Number.isFinite(subscription.amount) &&
        subscription.amount > 0
      ) {
        const paymentResult = await adminPaymentLinkService.createPaymentLink({
          clientId: subscription.clientId || undefined,
          clientName: subscription.clientName,
          clientEmail: subscription.clientEmail,
          clientPhone: subscription.clientPhone || undefined,
          clientCompany: subscription.clientCompany || undefined,
          serviceId: subscription.serviceId || undefined,
          serviceName: subscription.serviceName,
          paymentMode: subscription.paymentMode || 'recurring',
          linkedSubscriptionId: subscription.id || undefined,
          linkedSubscriptionNumber: subscription.subscriptionNumber || undefined,
          recurringInterval: subscription.interval,
          recurringStartDate: subscription.nextBillingDate || undefined,
          recurringAmount: Number(subscription.amount || 0),
          amount: Number(subscription.amount || 0),
          currencyCode: subscription.currencyCode || displayCurrencyCode,
          notes: subscription.internalNotes || undefined,
          clientMessage: subscription.notes || undefined,
          sendEmail: true,
          emailTemplateAlias: 'checkout-quote',
        });

        subscriptionToSave = {
          ...subscription,
          paymentLinkKey: paymentResult.paymentLink.paymentLinkKey,
          paymentMode:
            paymentResult.paymentLink.paymentMode || subscription.paymentMode || 'recurring',
          mollieCustomerId: paymentResult.paymentLink.mollieCustomerId || '',
          mollieSubscriptionId: paymentResult.paymentLink.mollieSubscriptionId || '',
          recurringInterval:
            paymentResult.paymentLink.recurringInterval || subscription.interval,
          recurringStatus: paymentResult.paymentLink.recurringStatus || '',
          recurringActivatedAt: paymentResult.paymentLink.recurringActivatedAt || '',
        };
        createdPaymentLink = true;
        paymentEmailSent = paymentResult.emailSent;
        paymentEmailError = paymentResult.emailError;
      }

      await adminSubscriptionService.saveSubscription(subscriptionToSave);
      await loadData();
      setSelectedSubscription(null);

      if (!isCreateMode) {
        toast.success('Abonnement mis a jour !');
        return;
      }

      if (createdPaymentLink && paymentEmailSent) {
        toast.success('Abonnement cree + lien de paiement envoye par email.');
        return;
      }

      if (createdPaymentLink && paymentEmailError) {
        toast.success('Abonnement cree + lien de paiement genere.');
        toast.error(`Email non envoye: ${paymentEmailError}`);
        return;
      }

      if (createdPaymentLink) {
        toast.success('Abonnement cree + lien de paiement genere.');
        return;
      }

      toast.success('Abonnement cree avec succes !');
      toast.error(
        'Lien de paiement non genere automatiquement (verifie service, email client et montant).'
      );
    } catch (error) {
      console.error(error);
      toast.error('Impossible de sauvegarder cet abonnement');
    }
  };

  const handleDelete = async (subscription: AdminSubscription) => {
    if (
      !window.confirm(
        `Supprimer l abonnement ${subscription.subscriptionNumber} ?`
      )
    ) {
      return;
    }

    try {
      await adminSubscriptionService.removeSubscription(subscription.id);
      await loadData();
      setSelectedSubscription(null);
      toast.success('Abonnement supprime !');
    } catch (error) {
      console.error(error);
      toast.error('Impossible de supprimer cet abonnement');
    }
  };

  const handleRunReminderSweep = async () => {
    const isConfirmed = window.confirm(
      'Envoyer maintenant les rappels abonnement dus aujourd hui ?'
    );
    if (!isConfirmed) {
      return;
    }

    setIsRunningReminderSweep(true);
    try {
      const payload = await adminSubscriptionReminderService.runAction('run');
      setReminderOverview(payload.overview || null);
      const report = payload.report;
      if (report) {
        toast.success(
          `${report.stats.sent} rappels envoyes, ${report.stats.skippedAlreadySent} deja journalises`
        );
      } else {
        toast.success('Rappels abonnement executes');
      }
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur rappels abonnement');
    } finally {
      setIsRunningReminderSweep(false);
    }
  };

  const handleSendReminderNow = async (subscription: AdminSubscription) => {
    if (!subscription.clientEmail.trim()) {
      toast.error('Aucun email client sur cet abonnement');
      return;
    }

    setSendingReminderForId(subscription.id);
    try {
      const payload = await adminSubscriptionReminderService.runAction('send_now', {
        subscriptionId: subscription.id,
      });
      setReminderOverview(payload.overview || null);
      toast.success(`Rappel envoye a ${subscription.clientEmail}`);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Envoi rappel impossible');
    } finally {
      setSendingReminderForId('');
    }
  };

  const handleMarkPaid = async (subscription: AdminSubscription) => {
    setModalActionInFlight('mark_paid');
    try {
      await adminSubscriptionService.markSubscriptionPaid(subscription);
      const refreshedSubscriptions = await loadData();
      setSelectedSubscription(
        refreshedSubscriptions.find((entry) => entry.id === subscription.id) || null
      );
      toast.success('Paiement marque comme recu');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Action impossible');
    } finally {
      setModalActionInFlight('');
    }
  };

  const handleDeferBilling = async (subscription: AdminSubscription, days: number) => {
    setModalActionInFlight(days === 3 ? 'defer_3' : 'defer_7');
    try {
      await adminSubscriptionService.deferSubscriptionBilling(subscription, days);
      const refreshedSubscriptions = await loadData();
      setSelectedSubscription(
        refreshedSubscriptions.find((entry) => entry.id === subscription.id) || null
      );
      toast.success(`Echeance decalee de ${days} jour(s)`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Decalage impossible');
    } finally {
      setModalActionInFlight('');
    }
  };

  const handleAddInternalNote = async (subscription: AdminSubscription) => {
    const note = window.prompt('Nouvelle note interne a ajouter');
    if (!note || !note.trim()) {
      return;
    }

    setModalActionInFlight('add_note');
    try {
      await adminSubscriptionService.addSubscriptionInternalNote(subscription, note);
      const refreshedSubscriptions = await loadData();
      setSelectedSubscription(
        refreshedSubscriptions.find((entry) => entry.id === subscription.id) || null
      );
      toast.success('Note interne ajoutee');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Ajout note impossible');
    } finally {
      setModalActionInFlight('');
    }
  };

  const handleSaveTeamRecord = async (
    subscriptionId: string,
    patch: Pick<TeamSubscriptionRecord, 'ownerName' | 'ownerEmail'>
  ) => {
    try {
      const next = {
        ...(teamRecords[subscriptionId] || {
          subscriptionId,
          ownerName: '',
          ownerEmail: '',
          updatedAt: '',
          tasks: [],
        }),
        ...patch,
        updatedAt: new Date().toISOString(),
      };
      const currentList = Object.values(teamRecords).filter((entry) => entry.subscriptionId !== subscriptionId);
      const saved = await adminTeamWorkspaceService.saveRecords([...currentList, next]);
      setTeamRecords(
        saved.reduce<Record<string, TeamSubscriptionRecord>>((acc, row) => {
          acc[row.subscriptionId] = row;
          return acc;
        }, {})
      );
      toast.success('Assignation responsable sauvegardee');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Sauvegarde assignation impossible');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Gestion des Abonnements
          </h2>
          <p className="mt-2 text-gray-600">
            Pilote les forfaits recurrents, les renouvellements, les retards et
            l avancement de delivery de chaque abonnement.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Affichage admin actuel: {displayCurrencyCode}. Les montants d origine restent
            conserves par abonnement.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleRunReminderSweep}
            disabled={isRunningReminderSweep}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-5 py-3 font-medium text-sky-800 transition-colors hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span>{isRunningReminderSweep ? 'Relances...' : 'Lancer rappels du jour'}</span>
          </button>
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-neon-orange px-5 py-3 text-white transition-colors hover:bg-[#FF4500]"
          >
            <span className="text-lg leading-none">+</span>
            <span>Nouvel abonnement</span>
          </button>
        </div>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Abonnements</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{metrics.total}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Actifs</div>
          <div className="mt-2 text-3xl font-bold text-emerald-600">{metrics.active}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">En retard</div>
          <div className="mt-2 text-3xl font-bold text-red-600">
            {reminderOverview?.stats.overdueCount ?? metrics.pastDue}
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">A risque</div>
          <div className="mt-2 text-3xl font-bold text-amber-600">
            {reminderOverview?.stats.atRiskCount ?? 0}
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">MRR suivi ({displayCurrencyCode})</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {currencyService.formatCurrencyAmount(metrics.revenue, displayCurrencyCode, {
              country: displayCountry,
              display: 'code',
            })}
          </div>
        </div>
      </div>

      <div className="mb-8 grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)]">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-700">
                Pilotage abonnement
              </div>
              <h3 className="mt-2 text-2xl font-bold text-gray-900">
                Priorites des 30 prochains jours
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Vue agence pour suivre les renouvellements, mandats et relances sans oublier un dossier.
              </p>
            </div>
            <div className="rounded-2xl bg-sky-50 px-4 py-3 text-right">
              <div className="text-xs uppercase tracking-[0.16em] text-sky-700">Rappels dus</div>
              <div className="mt-1 text-2xl font-bold text-sky-900">
                {reminderOverview?.stats.remindersDueTodayCount ?? 0}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl bg-amber-50 p-4">
              <div className="text-xs uppercase tracking-[0.16em] text-amber-700">
                Echeances ≤ 10j
              </div>
              <div className="mt-2 text-2xl font-bold text-amber-900">
                {reminderOverview?.stats.dueSoonCount ?? 0}
              </div>
            </div>
            <div className="rounded-2xl bg-orange-50 p-4">
              <div className="text-xs uppercase tracking-[0.16em] text-orange-700">
                Mandats pending
              </div>
              <div className="mt-2 text-2xl font-bold text-orange-900">
                {reminderOverview?.stats.mandatePendingCount ?? 0}
              </div>
            </div>
            <div className="rounded-2xl bg-emerald-50 p-4">
              <div className="text-xs uppercase tracking-[0.16em] text-emerald-700">
                Rappel mensuel unique
              </div>
              <div className="mt-2 text-2xl font-bold text-emerald-900">
                J-{reminderOverview?.stats.monthlyReminderDay ?? 10} ·{' '}
                {reminderOverview?.stats.dueForReminderDayCount ?? 0}
              </div>
            </div>
            <div className="rounded-2xl bg-indigo-50 p-4 md:col-span-3 xl:col-span-3">
              <div className="text-xs uppercase tracking-[0.16em] text-indigo-700">
                Politique relance
              </div>
              <div className="mt-2 text-sm font-medium text-indigo-900">
                Une seule relance par cycle de facturation (pas de J-3 / J-0 en plus).
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-900">Dernieres relances</div>
              <div className="text-xs text-gray-500">
                Template: {reminderOverview?.settings.templateAlias || 'checkout-upcoming-billing-reminder'}
              </div>
            </div>
            <div className="mt-3 space-y-3">
              {(reminderOverview?.recentLog || []).slice(0, 4).map((entry) => (
                <div
                  key={entry.id}
                  className="flex flex-col gap-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {entry.subscriptionNumber} • {entry.clientName || entry.clientEmail}
                    </div>
                    <div className="text-gray-500">
                      {entry.serviceName || 'Abonnement'} • cible {formatDate(entry.targetDate)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        entry.status === 'sent'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {entry.status === 'sent' ? 'Envoye' : 'Echec'}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{formatDateTime(entry.sentAt)}</div>
                  </div>
                </div>
              ))}
              {(!reminderOverview?.recentLog || reminderOverview.recentLog.length === 0) && (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
                  Aucun rappel journalise pour le moment.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm font-semibold uppercase tracking-[0.18em] text-neon-orange">
            Calendrier
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                Vue mensuelle renouvellements & rappels
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Echeances et points de contact a venir sur les {reminderOverview?.settings.lookaheadDays ?? 30} prochains jours.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCalendarMonthOffset((current) => current - 1)}
                className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
              >
                ←
              </button>
              <div className="min-w-[160px] text-center text-sm font-semibold text-gray-900">
                {calendarReferenceDate.toLocaleDateString('fr-FR', {
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </div>
              <button
                type="button"
                onClick={() => setCalendarMonthOffset((current) => current + 1)}
                className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
              >
                →
              </button>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((label) => (
              <div key={label} className="py-2">
                {label}
              </div>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-7 gap-2">
            {calendarDays.map((day) => {
              const entries = agendaByDate[day.key] || [];
              return (
                <div
                  key={day.key}
                  className={`min-h-[124px] rounded-2xl border p-3 ${
                    day.inMonth ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50/70'
                  } ${day.isToday ? 'ring-2 ring-neon-orange/30' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm font-semibold ${
                        day.inMonth ? 'text-gray-900' : 'text-gray-400'
                      }`}
                    >
                      {day.dayNumber}
                    </span>
                    {entries.length > 0 && (
                      <span className="rounded-full bg-neon-orange/10 px-2 py-1 text-[11px] font-semibold text-neon-orange">
                        {entries.length}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 space-y-2">
                    {entries.slice(0, 3).map((entry, index) => (
                      <div
                        key={`${entry.type}_${entry.subscriptionId}_${index}`}
                        className={`rounded-xl px-2.5 py-2 text-[11px] leading-4 ${
                          entry.type === 'renewal'
                            ? 'bg-orange-50 text-orange-800'
                            : 'bg-sky-50 text-sky-800'
                        }`}
                      >
                        <div className="font-semibold">
                          {entry.type === 'renewal' ? 'Renewal' : `J-${entry.daysBefore}`}
                        </div>
                        <div className="mt-1 truncate">{entry.clientName}</div>
                      </div>
                    ))}
                    {entries.length > 3 && (
                      <div className="text-[11px] font-medium text-gray-500">
                        +{entries.length - 3} autres
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 space-y-3">
            {(reminderOverview?.agenda || []).slice(0, 6).map((entry, index) => (
              <div
                key={`${entry.type}_${entry.subscriptionId}_${entry.date}_${index}`}
                className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${
                          entry.type === 'renewal'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-sky-100 text-sky-700'
                        }`}
                      >
                        {entry.type === 'renewal' ? 'Renouvellement' : `Rappel J-${entry.daysBefore}`}
                      </span>
                      <span className="text-xs text-gray-500">
                        {entry.daysUntil === 0 ? 'Aujourd hui' : `Dans ${entry.daysUntil} j`}
                      </span>
                    </div>
                    <div className="mt-2 font-medium text-gray-900">
                      {entry.subscriptionNumber} • {entry.clientName}
                    </div>
                    <div className="text-sm text-gray-600">{entry.serviceName}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{formatDate(entry.date)}</div>
                    <div className="text-sm text-gray-500">
                      {currencyService.formatCurrencyAmount(entry.amount, entry.currencyCode, {
                        display: 'code',
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(!reminderOverview?.agenda || reminderOverview.agenda.length === 0) && (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-sm text-gray-500">
                Aucune echeance a afficher pour le moment.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px_240px]">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Recherche
            </label>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Abonnement, client, email, service, commande..."
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as 'all' | SubscriptionStatus)
              }
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Frequence
            </label>
            <select
              value={intervalFilter}
              onChange={(event) =>
                setIntervalFilter(event.target.value as 'all' | SubscriptionInterval)
              }
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            >
              {INTERVAL_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {OPS_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() =>
                setOpsFilter(
                  filter.value as 'all' | 'due_soon' | 'at_risk' | 'mandate_pending'
                )
              }
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                opsFilter === filter.value
                  ? 'bg-neon-orange text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-gray-600">
          Chargement des abonnements...
        </div>
      ) : filteredSubscriptions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
          <h3 className="text-2xl font-semibold text-gray-900">
            Aucun abonnement pour le moment
          </h3>
          <p className="mx-auto mt-3 max-w-2xl text-gray-600">
            Cree le premier forfait recurrent pour suivre le statut, la facturation et
            le delivery de maniere propre dans l admin.
          </p>
          <button
            onClick={handleOpenCreate}
            className="mt-8 rounded-lg bg-neon-orange px-5 py-3 font-medium text-white transition-colors hover:bg-[#FF4500]"
          >
            Creer un abonnement
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubscriptions.map((subscription) => {
            const riskMeta = getRiskMeta(subscription);
            const displayAmount = currencyService.formatAmountForDisplay(
              subscription.amount,
              subscription.currencyCode,
              {
                country: displayCountry,
                display: 'code',
              }
            );
            const originalAmount = currencyService.formatCurrencyAmount(
              subscription.amount,
              subscription.currencyCode,
              { display: 'code' }
            );
            const lastReminder = reminderOverview?.lastSentBySubscription?.[subscription.id];

            return (
              <motion.div
                key={subscription.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {subscription.subscriptionNumber}
                      </h3>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          STATUS_BADGES[subscription.status]
                        }`}
                      >
                        {STATUS_LABELS[subscription.status]}
                      </span>
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                        {INTERVAL_LABELS[subscription.interval]}
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${riskMeta.classes}`}
                      >
                        {riskMeta.label}
                      </span>
                    </div>

                    <div className="grid gap-3 text-sm text-gray-600 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Client
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          {subscription.clientName}
                        </div>
                        <div>{subscription.clientEmail}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Service
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          {subscription.serviceName || 'Non renseigne'}
                        </div>
                        <div>{subscription.clientCompany || 'Sans entreprise'}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Facturation
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          Prochaine: {formatDate(subscription.nextBillingDate)}
                        </div>
                        <div>Derniere: {formatDate(subscription.lastBillingDate)}</div>
                        <div className="text-xs text-gray-500">Risque: {riskMeta.reason}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Montant
                        </div>
                        <div className="mt-1 text-xl font-semibold text-neon-orange">
                          {displayAmount}
                        </div>
                        <div className="text-xs text-gray-500">Base: {originalAmount}</div>
                        <div className="text-xs text-gray-500">
                          Dernier rappel: {lastReminder ? formatDateTime(lastReminder.sentAt) : 'Aucun'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleSendReminderNow(subscription)}
                      disabled={
                        sendingReminderForId === subscription.id ||
                        subscription.paymentMode !== 'recurring' ||
                        !subscription.clientEmail.trim()
                      }
                      className="rounded-lg bg-sky-50 px-4 py-2 font-medium text-sky-700 transition-colors hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sendingReminderForId === subscription.id
                        ? 'Envoi...'
                        : 'Envoyer rappel'}
                    </button>
                    <button
                      onClick={() => handleOpenView(subscription)}
                      className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                    >
                      Ouvrir
                    </button>
                    <button
                      onClick={() => handleMarkPaid(subscription)}
                      className="rounded-lg bg-emerald-50 px-4 py-2 font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
                    >
                      Marquer paye
                    </button>
                    <button
                      onClick={() => handleDelete(subscription)}
                      className="rounded-lg bg-red-50 px-4 py-2 font-medium text-red-600 transition-colors hover:bg-red-100"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {selectedSubscription && (
        <SubscriptionDetailsModal
          subscription={selectedSubscription}
          teamRecord={teamRecords[selectedSubscription.id] || null}
          clients={clients}
          services={services}
          defaultMode={modalMode}
          onClose={() => setSelectedSubscription(null)}
          onSave={handleSave}
          onMarkPaid={handleMarkPaid}
          onDeferBilling={handleDeferBilling}
          onAddInternalNote={handleAddInternalNote}
          onSaveTeamRecord={handleSaveTeamRecord}
          actionInFlight={modalActionInFlight}
        />
      )}
    </div>
  );
};

export default SubscriptionManager;
